import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { setModalId, usePublicState } from '@/store/slices/publicSlice';
import {
  setTotalRevenueFilters,
  useTotalRevenueFilters,
} from '@/store/slices/reportFiltersSlice';
import CustomModal from '@/components/atoms/CustomModal';
import InfoText from '@/components/atoms/InfoText';
import FormField from '@/components/FormField/FormField';
import Button from '@/components/atoms/Button';
import { useGetTotalRevenueRequestTypesLKQuery } from '@/store/services/reports';
import DatesFilters from '../SharedComponents/DatesFilters';
import useDefaultReportDates from '../useDefaultReportDates';
import { useSharedFunctions } from '../SharedComponents/useSharedFunctions';

interface FormValues {
  requestTypes?: string[];
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
}

export default function FilterModal() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { modalId } = usePublicState();
  const filters = useTotalRevenueFilters();
  const { defaultFromDate, defaultToDate } = useDefaultReportDates();
  const { resetPage } = useSharedFunctions();

  const { data: requestTypesData, isLoading: isRequestTypesLoading } =
    useGetTotalRevenueRequestTypesLKQuery();

  const schema = z
    .object({
      requestTypes: z.array(z.string()).optional(),
      fromDate: z.custom<Dayjs>().nullable(),
      toDate: z.custom<Dayjs>().nullable(),
    })
    .superRefine(({ fromDate, toDate }, ctx) => {
      if (fromDate && toDate && toDate.isBefore(fromDate, 'day')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(
            'dispute.dateToBeforeFrom',
            'Date to must be after date from'
          ),
          path: ['toDate'],
        });
      }
    });

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'all',
    values: {
      requestTypes: Array.isArray(filters?.requestTypes)
        ? filters?.requestTypes
        : [],
      fromDate: filters.fromDate ? dayjs(filters.fromDate) : null,
      toDate: filters.toDate ? dayjs(filters.toDate) : null,
    },
  });

  const onClose = () => dispatch(setModalId(null));

  const isModalOpen = modalId === 'TOTAL_REVENUE_REPORT_MODAL';

  const watchAll = methods.watch();

  const appliedFiltersCount = [
    !!watchAll.requestTypes?.length,
    watchAll.fromDate,
    watchAll.toDate,
  ].filter(Boolean).length;

  const onReset = () => {
    methods.reset({
      requestTypes: [],
      fromDate: defaultFromDate,
      toDate: defaultToDate,
    });
  };

  const onSubmit = (values: FormValues) => {
    const fromDateStr = values.fromDate
      ? values.fromDate.format('YYYY-MM-DD')
      : '';
    const toDateStr = values.toDate ? values.toDate.format('YYYY-MM-DD') : '';

    dispatch(
      setTotalRevenueFilters({
        requestTypes: values.requestTypes || [],
        fromDate: fromDateStr,
        toDate: toDateStr,
      })
    );

    resetPage();
    onClose();
  };

  const requestTypesList = Array.isArray(requestTypesData)
    ? requestTypesData
    : (requestTypesData as any)?.payload || [];

  const requestTypeOptions =
    requestTypesList?.map((item: any) => ({
      label: item.name || item.label || item,
      value: item.id || item.value || item,
    })) || [];

  return (
    <CustomModal
      title={t('home.filters')}
      isOpen={isModalOpen}
      onClose={onClose}
      subTitle={
        <div className="flex items-center gap-2">
          <InfoText
            size="sm"
            text={`${appliedFiltersCount} ${t('home.filterApplied')}`}
          />
          <button
            type="button"
            onClick={onReset}
            className="text-light-success hover:underline text-sm font-semibold"
          >
            {t('home.resetFilter')}
          </button>
        </div>
      }
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-6">
          {/* Request Type Section */}
          <div className="space-y-2">
            <h3 className="font-semibold text-base text-[#141414]">
              {t('reports.revenue.requestType', 'Request type')}
            </h3>
            <FormField
              name="requestTypes"
              id="requestTypes"
              type="select"
              placeholder={t(
                'reports.revenue.selectRequestType',
                'Select request type'
              )}
              options={requestTypeOptions}
              loading={isRequestTypesLoading}
              mode="multiple"
              allowClear
            />
          </div>

          <hr className="border-t border-[#F0F0F0]" />

          {/* Date Range Section */}
          <DatesFilters />

          {/* Actions */}
          <div className="flex items-center justify-end gap-x-4 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              className="min-w-[120px] h-[48px]"
            >
              {t('home.cancel')}
            </Button>
            <Button
              type="submit"
              variant="dark"
              className="min-w-[120px] h-[48px]"
            >
              {t('home.save')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </CustomModal>
  );
}
